/* ---------------------------------------------------------------------------
(c) 2023-2024, HID Global Corporation, part of ASSA ABLOY.
All rights reserved.

This program is an un published copyrighted work which is proprietary
to HID Global Corporation. This computer program includes Confidential,
Proprietary Information and is a Trade Secret of HID Global Corporation.
Any use, disclosure, modification and/or reproduction is prohibited
unless authorized in writing by HID Global Corporation.

WARNING:  Unauthorized reproduction of this program as well as
unauthorized preparation of derivative works based upon the
program or distribution of copies by sale, rental, lease or
lending are violations of federal copyright laws and state trade
secret laws, punishable by civil and criminal penalties.
--------------------------------------------------------------------------- */
import { Component, OnInit } from '@angular/core';
import { MatIconModule, MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { BehaviorSubject, interval, Subscription } from 'rxjs';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import moment from 'moment';

import { SharedService } from './shared/services/shared.service';
import { AuthService } from './auth/auth.service';
import { SessionTimeoutComponent } from './shared/components/session-timeout/session-timeout.component';
import { AlertComponent } from './shared/components/alert/alert.component';
import { HeaderComponent } from './layout/header/header.component';
import { NotificationData, NotificationService } from './shared/services/notification.service';
import { NotificationComponent } from './shared/components/notification/notification.component';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  imports: [CommonModule, RouterModule, MatIconModule, AlertComponent, NotificationComponent]
})
export class AppComponent implements OnInit {
  show: boolean = true;
  isAuthenticated: boolean = false;
  showHeader: boolean = false;
  helpUrl!: string;
  tenantId: any;

  // session timeout variables
  subscription!: Subscription;
  dialogOpened: boolean = false;
  dialogRef: any;
  logoutRef: any = false;
  dialogData = new BehaviorSubject<any>({});
  actionFlag: any = {
    continue_session: true,
    login: true
  };
  dialogActions: any[] = [];
  milliSecondsInASecond = 1000;

  // can be removed
  hoursInADay = 24;
  minutesInAnHour = 60;
  SecondsInAMinute = 60;
  secondsToDday!: number;
  minutesToDday!: number;
  hoursToDday!: number;
  daysToDday!: number;

  idleSecondsToDday!: number;
  idleMinutesToDday!: number;
  idleHoursToDday!: number;
  idleDaysToDday!: number;
  sessionExpired: boolean = false;
  sessionExpiredMessage = 'Your session has expired. Please log in again.';
  notification: NotificationData | null = null;

  constructor(
    private sharedService: SharedService,
    private authService: AuthService,
    private matIconRegistry: MatIconRegistry,
    private domSanitizer: DomSanitizer,
    private dialog: MatDialog,
    private notificationService: NotificationService
  ) {}

  ngOnInit() {
    this.matIconRegistration();
    this.tenantId = this.authService.getTenantId();
    this.sharedService.loaderState.subscribe((state) => {
      setTimeout(() => {
        this.show = state;
      });
    });
    this.authService.isLoggedIn.subscribe((data) => {
      if (data) {
        setTimeout(() => {
          this.isAuthenticated = true;
          if (this.authService.getDecodedInfo('expiresIn')) {
            this.startTimer();
          }
        });
      } else {
        this.isAuthenticated = false;
      }
    });

    this.initNotificationService();
  }

  private initNotificationService(): void {
    this.notificationService.notification$.subscribe((data) => {
      this.notification = data;

      // Auto-hide after 3 seconds
      setTimeout(() => (this.notification = null), 3000);
    });
  }

  closeNotification(data: boolean) {
    this.notification = null;
  }

  private matIconRegistration(): void {
    const iconList = [
      'warning_outline',
      'working',
      'locked',
      'expired',
      'password',
      'hid_approve',
      'otp_token',
      'disabled',
      'crescendo_key',
      'slider_arrow_prev',
      'slider_arrow_next',
      'fido',
      'fal_circle_user',
      'help_outline',
      'info',
      'not_found',
      'card',
      'contactless_card_inactive',
      'contactless_card_active',
      'warning_disabled',
      'warning_alert',
      'pin'
    ];

    iconList.forEach((icon) => {
      this.matIconRegistry.addSvgIcon(
        icon,
        this.domSanitizer.bypassSecurityTrustResourceUrl('assets/images/icons/' + icon + '.svg')
      );
    });
  }

  // logout from the application
  logout(revokeToken = true): void {
    this.unsubscribe();
    this.logoutRef = true;
    if (revokeToken) {
      this.authService.revokeToken();
    } else {
      this.authService.logout();
    }
  }

  private startTimer(): void {
    if (this.subscription === undefined) {
      this.subscription = interval(1000).subscribe(() => {
        if (this.authService.getDecodedInfo('inactivityTimeValidDuration') !== undefined) {
          this.getTimeDifference();
        }
      });
    }
  }

  private getTimeDifference() {
    const now = moment().valueOf(); //current date value
    const sessionTime = moment(this.authService.getDecodedInfo('expiresIn') as string).valueOf(); // expires in date
    const idleTimeOut = moment(this.authService.getDecodedInfo('inactivityTimeValidDuration') as string).valueOf();
    const sessionTimeDifference = sessionTime - now;
    const idleTimeOutDifference = idleTimeOut - now;
    const idleTimeChecker = idleTimeOut - moment(this.milliSecondsInASecond * 60).valueOf() - now; // buffer of 1 minute

    if (!this.logoutRef) {
      //if logout called manually, do not check for any sessions
      this.checkSession(sessionTimeDifference, idleTimeOutDifference, idleTimeChecker);
    }
  }

  private checkSession(sessionTimeDifference: number, idleTimeOutDifference: number, idleTimeChecker: number) {
    //if logout called manually, do not check for any sessions
    if (sessionTimeDifference > 0) {
      this.allocateTimeUnits(sessionTimeDifference);
      if (idleTimeOutDifference > 0) {
        this.allocateIdleTimeUnits(idleTimeOutDifference);
        if (idleTimeChecker < 0) {
          // dialog for session timeout for 9 min
          this.checkAction('CONTINUE_SESSION', false);
          if (this.idleSecondsToDday === 0 || this.idleSecondsToDday < 0) {
            setTimeout(() => {
              this.sessionTimeout();
            }, 1000);
          }
        }
      } else {
        // dialog for session timeout for 10 min
        this.sessionTimeout();
      }
    } else {
      this.subscription.unsubscribe();
      // dialog for session timeout for 60 min
      this.sessionTimeout();
    }
  }

  private sessionTimeout() {
    if (location.href !== location.origin + '/' + this.tenantId) {
      this.checkAction('LOGIN');
      this.logout(); // revoke token to false
      this.sessionExpired = true;
    }
  }

  private checkAction(action: string, unsubscribe: boolean = true): void {
    if (this.dialogActions.indexOf(action) === -1) {
      const popupData = this.formPopupData(action);
      this.dialogActions.push(action);
      this.dialogData.next(popupData); // set behaviour subject
      if (this.dialogData.value['action'] === 'CONTINUE_SESSION') {
        this.openDialogAndUnsubscribe(unsubscribe);
      } else {
        if (this.dialogRef) {
          this.dialogRef.close();
        }
      }
    }
  }

  // form the modal data based on action
  private formPopupData(action: any): any {
    const continueSessionMessage =
      'Your session is expiring in <strong>{{delay}} secs</strong>. Do you want to continue?';
    return {
      title: 'Session Expiry',
      message: action === 'CONTINUE_SESSION' ? continueSessionMessage : this.sessionExpiredMessage,
      action: action === 'CONTINUE_SESSION' ? 'CONTINUE_SESSION' : 'LOGIN',
      icon: {
        show: true,
        name: action === 'CONTINUE_SESSION' ? 'warning_outline' : 'error',
        custom: action === 'CONTINUE_SESSION'
      },
      button: {
        logout: action === 'CONTINUE_SESSION'
      },
      delay: this.idleSecondsToDday
    };
  }

  private openDialogAndUnsubscribe(unsubscribe: boolean = true): void {
    this.openSessionDialog();
    if (unsubscribe) {
      this.unsubscribe();
    }
  }

  private openSessionDialog(): void {
    let dialogConfig = new MatDialogConfig();
    if (!this.dialogOpened) {
      // open the dialog if the value is false
      dialogConfig = {
        maxWidth: '500px',
        position: {
          top: '125px'
        },
        disableClose: true,
        autoFocus: false,
        data: this.dialogData
      };
      this.dialogRef = this.dialog.open(SessionTimeoutComponent, dialogConfig);
      this.dialogOpened = true;
    } else {
      // if the dialog is already opened
      this.dialogRef.componentInstance.data = this.dialogData;
    }

    this.dialogRef.componentInstance.successEvent.subscribe((data: any) => {
      if (data === 'CONTINUE_SESSION') {
        if (this.actionFlag.continue_session) {
          this.actionFlag.continue_session = false;
          //extend the session for 10 minutes again if they click on ok
          this.sharedService.userInfo(this.tenantId as string).subscribe({
            next: (data) => {
              if (data) {
                this.continueSession();
              }
            },
            error: () => {
              this.continueSession();
            }
          });
        }
      }
    });

    //logout  on cancel click
    this.dialogRef.componentInstance.cancel.subscribe((data: any) => {
      if (data) {
        this.dialogOpened = false; // set the value to false
        this.dialogRef.close();
        this.logout(true);
      }
    });
  }

  private continueSession(): void {
    this.authService.addToStorage({
      inactivityTimeValidDuration: moment()
        .add(this.authService.getDecodedInfo('inactivityTimeDuration') as string, 'milliseconds')
        .format()
    });
    this.dialogOpened = false; // set the value to false
    const removeIndex = this.dialogActions.indexOf('CONTINUE_SESSION');
    this.dialogActions.splice(removeIndex);
    this.actionFlag.continue_session = true;
    this.dialogRef.close();
  }

  private unsubscribe(): void {
    this.subscription.unsubscribe();
  }

  private allocateTimeUnits(timeDifference: any): void {
    this.secondsToDday = Math.floor((timeDifference / this.milliSecondsInASecond) % this.SecondsInAMinute);
    this.minutesToDday = Math.floor(
      (timeDifference / (this.milliSecondsInASecond * this.minutesInAnHour)) % this.SecondsInAMinute
    );
    this.hoursToDday = Math.floor(
      (timeDifference / (this.milliSecondsInASecond * this.minutesInAnHour * this.SecondsInAMinute)) % this.hoursInADay
    );
    this.daysToDday = Math.floor(
      timeDifference / (this.milliSecondsInASecond * this.minutesInAnHour * this.SecondsInAMinute * this.hoursInADay)
    );
  }

  private allocateIdleTimeUnits(idleTimeOutDifference: any): void {
    this.idleSecondsToDday = Math.floor((idleTimeOutDifference / this.milliSecondsInASecond) % this.SecondsInAMinute);
    this.idleMinutesToDday = Math.floor(
      (idleTimeOutDifference / (this.milliSecondsInASecond * this.minutesInAnHour)) % this.SecondsInAMinute
    );
    this.idleHoursToDday = Math.floor(
      (idleTimeOutDifference / (this.milliSecondsInASecond * this.minutesInAnHour * this.SecondsInAMinute)) %
        this.hoursInADay
    );
    this.idleDaysToDday = Math.floor(
      idleTimeOutDifference /
        (this.milliSecondsInASecond * this.minutesInAnHour * this.SecondsInAMinute * this.hoursInADay)
    );
  }
}
